export interface JsonLd {
  '@context': string;
  '@type': string;
  [key: string]: unknown;
}

export interface SeoMeta {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: string;
  /** robots directive; 'noindex, nofollow' to exclude a page from search. */
  robots?: string;
  /** Canonical URL. Defaults to the current path (origin + pathname). */
  canonical?: string;
  /** Structured data (schema.org JSON-LD). One or many blocks. */
  jsonLd?: JsonLd | JsonLd[];
}

export const SITE_NAME = 'BluePages';
export const BASE_URL = 'https://bluepages.co.za';
export const DEFAULT_TITLE = `${SITE_NAME} — South Africa's skills-first job marketplace`;
export const DEFAULT_DESCRIPTION =
  'BluePages is South Africa’s skills-first job marketplace. Match your skills to the right roles, upskill with recognised training, and get hired. ';
export const DEFAULT_IMAGE = `${BASE_URL}/og-image.png`;
export const DEFAULT_IMAGE_SQUARE = `${BASE_URL}/og-image-square.png`;
export const DEFAULT_ROBOTS = 'index, follow, max-image-preview:large';
export const LOCALE = 'en-ZA';

/** Resolve the canonical URL for the current location (origin + path, no hash/query). */
export function currentCanonical(): string {
  if (typeof window === 'undefined') return BASE_URL;
  return window.location.origin + window.location.pathname;
}

function upsertMeta(key: 'name' | 'property', value: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${key}="${value}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(key, value);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string, extra?: Record<string, string>) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"][hreflang="${extra?.hreflang ?? ''}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    if (extra?.hreflang) el.setAttribute('hreflang', extra.hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/**
 * Inject (or replace) a JSON-LD script block. Keyed by `id` so the same block
 * can be cleanly updated on navigation and removed on unmount.
 */
export function setJsonLd(id: string, data: JsonLd | JsonLd[]) {
  const blocks = Array.isArray(data) ? data : [data];
  let script = document.head.querySelector<HTMLScriptElement>(`script[data-seo-jsonld="${id}"]`);
  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo-jsonld', id);
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(blocks.length === 1 ? blocks[0] : blocks);
}

export function removeJsonLd(id: string) {
  document.head.querySelector<HTMLScriptElement>(`script[data-seo-jsonld="${id}"]`)?.remove();
}

/**
 * Update the document's SEO tags (title, description, canonical, Open Graph,
 * Twitter/X, robots, hreflang and JSON-LD). Tags already declared in index.html
 * are updated in place; missing ones are created.
 *
 * Note: Googlebot renders JS, so these values do get indexed for client-side
 * routes — but the static index.html is the fallback for crawlers that do not
 * execute scripts, so keep the platform defaults in index.html current.
 *
 * Call `resetSeo()` on unmount so navigation returns to platform defaults and
 * stale JSON-LD blocks are cleared.
 */
export function setSeo(meta: SeoMeta = {}) {
  const title = meta.title ?? DEFAULT_TITLE;
  const description = meta.description ?? DEFAULT_DESCRIPTION;
  const image = meta.image ?? DEFAULT_IMAGE;
  const type = meta.type ?? 'website';
  const url = meta.url ?? currentCanonical();
  const canonical = meta.canonical ?? currentCanonical();
  const robots = meta.robots ?? DEFAULT_ROBOTS;

  document.title = title;
  upsertMeta('name', 'description', description);
  upsertMeta('name', 'robots', robots);

  upsertCanonical(canonical);

  // Self-referential hreflang for the single en-ZA locale + x-default.
  upsertLink('alternate', canonical, { hreflang: LOCALE });
  upsertLink('alternate', BASE_URL + '/', { hreflang: 'x-default' });

  // Open Graph
  upsertMeta('property', 'og:site_name', SITE_NAME);
  upsertMeta('property', 'og:title', title);
  upsertMeta('property', 'og:description', description);
  upsertMeta('property', 'og:type', type);
  upsertMeta('property', 'og:url', url);
  upsertMeta('property', 'og:locale', LOCALE.replace('-', '_'));
  upsertMeta('property', 'og:image', image);
  upsertMeta('property', 'og:image:secure_url', image);

  // Twitter / X
  upsertMeta('name', 'twitter:card', 'summary_large_image');
  upsertMeta('name', 'twitter:title', title);
  upsertMeta('name', 'twitter:description', description);
  upsertMeta('name', 'twitter:image', image);

  if (meta.jsonLd) {
    setJsonLd('page', meta.jsonLd);
  }
}

/** Reset to platform defaults and clear per-page JSON-LD. */
export function resetSeo() {
  removeJsonLd('page');
  setSeo({});
}
